
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL!,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY!
);

export default function Login() {
  const [credentials, setCredentials] = useState({ phone: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // 验证手机号格式
  const isValidPhone = (phone: string) => {
    const phoneRegex = /^1[3-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    // 验证手机号和密码
    if (!credentials.phone.trim() || !credentials.password.trim()) {
      setError('请输入手机号和密码');
      setIsLoading(false);
      return;
    }

    // 验证手机号格式
    if (!isValidPhone(credentials.phone)) {
      setError('请输入正确的手机号格式');
      setIsLoading(false);
      return;
    }
    
    try {
      // 调用 Supabase Edge Function 进行数据库验证
      const { data, error: functionError } = await supabase.functions.invoke('mysql-auth', {
        body: {
          phone_number: credentials.phone,
          password: credentials.password
        }
      });

      if (functionError) {
        console.error('Edge Function 调用失败:', functionError);
        setError('登录服务暂时不可用，请稍后重试');
        setIsLoading(false);
        return;
      }

      if (data.error) {
        setError(data.error);
        setIsLoading(false);
        return;
      }

      if (data.success && data.user) {
        // 存储用户信息到本地存储
        const userInfo = data.user;
        localStorage.setItem('userRole', userInfo.role || 'employee');
        localStorage.setItem('userPhone', userInfo.phone_number);
        localStorage.setItem('userName', userInfo.user_name_cn || userInfo.user_name_en || userInfo.phone_number);
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('loginTime', new Date().toISOString());
        
        // 存储完整的用户信息供个人资料页面使用
        localStorage.setItem('userProfile', JSON.stringify(userInfo));
        
        console.log('用户登录成功:', userInfo);
        
        setIsLoading(false);
        navigate('/dashboard');
      } else {
        setError('登录失败，请检查手机号和密码');
        setIsLoading(false);
      }

    } catch (error) {
      console.error('登录过程出现错误:', error);
      setError('网络错误，请检查网络连接后重试');
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: 'phone' | 'password', value: string) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
    if (error) setError(''); // 清除错误信息
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-building-line text-2xl text-white"></i>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">企业工作台</h1>
            <p className="text-gray-600">使用手机号登录您的工作账户</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <i className="ri-error-warning-line text-red-500 mr-2"></i>
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                手机号码
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-phone-line text-gray-400"></i>
                </div>
                <input
                  type="tel"
                  required
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors ${
                    error ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="请输入11位手机号码"
                  value={credentials.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  maxLength={11}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                密码
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-lock-line text-gray-400"></i>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors ${
                    error ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="请输入密码"
                  value={credentials.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-gray-400 hover:text-gray-600`}></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  正在验证...
                </div>
              ) : (
                '登录'
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="text-center">
              <p className="text-xs text-gray-500">
                企业级安全登录 · 数据加密传输 · 数据库实时验证
              </p>
              <p className="text-xs text-indigo-600 mt-1">
                使用数据库中的真实用户信息进行身份验证
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
